import axios from '../config/axios';

export interface UploadedAttachmentResponse {
  id: number;
  title: string;
  originalName: string;
  fileName: string;
  fileUrl: string;
  signedUrl?: string;
  fileSize: number;
  mimeType: string;
  tenantId: number;
  createdByIdUser?: number | null;
  createdAt: string;
  usageCount?: number;
  hasUsage?: boolean;
}

export interface AttachmentUsage {
  module: string;
  moduleLabel: string;
  entityId: number;
  recordName: string;
  isCover?: boolean;
  editUrl?: string;
}

export type GalleryFile = UploadedAttachmentResponse;

export interface GalleryQueryOptions {
  page?: number;
  limit?: number;
  search?: string;
  type?: 'all' | 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other';
  usage?: 'all' | 'used' | 'unused' | string;
  sortBy?: 'recent' | 'oldest' | 'name_asc' | 'name_desc' | 'size_asc' | 'size_desc';
}

export interface GalleryResponse {
  data: UploadedAttachmentResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class AttachmentApiService {
  private static readonly BASE_URL = '/attachments';

  static async upload(file: File, customTitle?: string): Promise<UploadedAttachmentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (customTitle && customTitle.trim()) {
      formData.append('title', customTitle.trim());
    }

    const response = await axios.post<{ data: UploadedAttachmentResponse }>(
      `${this.BASE_URL}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  static async getGallery(options: GalleryQueryOptions = {}): Promise<GalleryResponse> {
    const response = await axios.get<GalleryResponse>(`${this.BASE_URL}/gallery`, {
      params: options,
    });
    return response.data;
  }

  static async getById(id: number): Promise<UploadedAttachmentResponse> {
    const response = await axios.get<{ data: UploadedAttachmentResponse }>(
      `${this.BASE_URL}/${id}`
    );
    return response.data.data;
  }

  static async getViewUrl(id: number): Promise<string> {
    const response = await axios.get<{ data: { url: string; expiresIn: string } }>(
      `${this.BASE_URL}/${id}/view-url`
    );
    return response.data.data.url;
  }

  static async getDownloadUrl(id: number): Promise<string> {
    const response = await axios.get<any>(
      `${this.BASE_URL}/${id}/download-url`
    );
    return response.data?.url || response.data?.data?.url || '';
  }

  static async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await axios.delete<{ success: boolean; message: string }>(
      `${this.BASE_URL}/${id}`
    );
    return response.data;
  }

  static async getUsage(id: number): Promise<AttachmentUsage[]> {
    const response = await axios.get<any>(
      `${this.BASE_URL}/${id}/usage`
    );
    const body = response.data;
    if (Array.isArray(body)) return body;
    if (Array.isArray(body?.data?.usages)) return body.data.usages;
    if (Array.isArray(body?.usages)) return body.usages;
    if (Array.isArray(body?.data)) return body.data;
    return [];
  }

  static async update(id: number, data: { title?: string; originalName?: string }): Promise<UploadedAttachmentResponse> {
    const response = await axios.patch<{ data: UploadedAttachmentResponse }>(
      `${this.BASE_URL}/${id}`,
      data
    );
    return response.data.data;
  }

  static async getSignedUrl(filePath: string, forceDownload = false, fileName?: string): Promise<string> {
    if (!filePath) return '';
    const response = await axios.get<{ url?: string; data?: { url?: string } }>(
      `${this.BASE_URL}/signed-url`,
      {
        params: {
          filePath,
          ...(forceDownload ? { forceDownload: 'true' } : {}),
          ...(fileName ? { fileName } : {}),
        },
      }
    );
    return response.data?.url || response.data?.data?.url || filePath;
  }

  static async getCoverSignedUrl(filePath: string): Promise<string> {
    if (!filePath) return '';
    const response = await axios.get<{ url?: string; data?: { url?: string } }>(
      `${this.BASE_URL}/cover-signed-url`,
      {
        params: { filePath },
      }
    );
    return response.data?.url || response.data?.data?.url || filePath;
  }

  static async getBase64(filePath: string): Promise<{ base64: string; mimeType: string; dataUrl: string }> {
    if (!filePath) return { base64: '', mimeType: '', dataUrl: '' };
    const response = await axios.get<{
      base64: string;
      mimeType: string;
      dataUrl: string;
      data?: { base64: string; mimeType: string };
    }>(`${this.BASE_URL}/base64`, {
      params: { filePath },
    });
    const resData = response.data;
    const base64 = resData.base64 || resData.data?.base64 || '';
    const mimeType = resData.mimeType || resData.data?.mimeType || 'application/octet-stream';
    const dataUrl = resData.dataUrl || `data:${mimeType};base64,${base64}`;
    return { base64, mimeType, dataUrl };
  }
}

export default AttachmentApiService;
